import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

import { PolicyService } from 'app/services/policy-service';
import { CommonsText } from 'app/commons/CommonsText';
import { ActionItem } from '../../components/actions-dropdown/actions-dropdown.component';

export interface InsuranceCard {
    nombre: string;
    polizaNumero: string;
    vigencia: string;
    estado: string;
    renovacionAutomatica: string;
    branchType: string;
    policyYear: string;
    policyId: any;
    isTaker: boolean;
    iconKey: string;
    acciones: ActionItem[];
}

@Component({
    selector: 'app-insurances-page',
    templateUrl: './insurances-page.component.html',
    styleUrls: ['./insurances-page.component.scss']
})
export class InsurancesPageComponent implements OnInit {

    loading: boolean = false;
    insurances: InsuranceCard[] = [];

    constructor(private router: Router,
                private policyService: PolicyService,
                private toastr: ToastrService) {}

    ngOnInit(): void {
        this.load();
    }

    trackByIndex(index: number): number {
        return index;
    }

    iconForCard(card: InsuranceCard): string {
        const base = 'resources/assets/images/main-page/';
        const key = ((card && (card.branchType || card.iconKey)) || '').toLowerCase().trim();
        const fileMap: { [k: string]: string } = {
            legal:        'onlygal-abogado-365.svg',
            abogado:      'onlygal-abogado-365.svg',
            lawyer:       'onlygal-abogado-365.svg',
            travel:       'onlygal-asistencia-viaje.svg',
            viaje:        'onlygal-asistencia-viaje.svg',
            trip:         'onlygal-asistencia-viaje.svg',
            compraventa:  'onlygal-compraventa-asegurada.svg',
            home:         'onlygal-compraventa-asegurada.svg',
            vivienda:     'onlygal-compraventa-asegurada.svg',
            inmueble:     'onlygal-compraventa-asegurada.svg',
            car:          'car.svg',
            coche:        'car.svg',
            vehiculo:     'car.svg',
            rent:         'rent.svg',
            family:       'onlygal-abogado-365.svg'
        };
        let file = 'bag-2.svg';
        for (const k of Object.keys(fileMap)) {
            if (key.indexOf(k) !== -1) { file = fileMap[k]; break; }
        }
        return base + file;
    }

    private buildAcciones(card: InsuranceCard): ActionItem[] {
        return [
            {
                label: 'Abrir expediente',
                icon: 'plus',
                action: () => this.irAbrirExpediente(card)
            },
            {
                label: 'Consulta de expedientes',
                icon: 'file',
                action: () => this.irConsultaExpedientes(card)
            },
            {
                label: 'Descarga PDF contrato',
                icon: 'file-pdf',
                // El contrato es del tomador: a un asegurado que no lo es no se le ofrece.
                visible: card.isTaker,
                action: () => this.descargarContrato(card)
            },
            {
                label: 'Ver detalle',
                icon: 'eye',
                action: () => this.irDetalle(card)
            }
        ];
    }

    private irDetalle(card: InsuranceCard): void {
        const num = card.polizaNumero;
        if (!num) { return; }
        this.router.navigate(['/products', num]);
    }

    private irAbrirExpediente(card: InsuranceCard): void {
        const num = card.polizaNumero;
        if (!num) { this.router.navigate(['/events/register']); return; }
        this.router.navigate(['/events/register', num]);
    }

    private irConsultaExpedientes(card: InsuranceCard): void {
        // El listado de expedientes se abre filtrado por la poliza de la tarjeta; sin numero,
        // se abre el listado completo.
        const num = card.polizaNumero;
        if (!num) { this.router.navigate(['/expedientes']); return; }
        this.router.navigate(['/expedientes'], { queryParams: { policy: num } });
    }

    private descargarContrato(card: InsuranceCard): void {
        if (card.policyId) {
            this.policyService.getContractFile(card.policyId);
        }
    }

    private load(): void {
        this.loading = true;
        this.policyService.getCurrent()
            .then(success => {
                this.loading = false;
                const list = (success && success.response) ? success.response : [];
                this.insurances = list.map(p => {
                    const card: InsuranceCard = {
                        nombre: p.product || '',
                        polizaNumero: (p.number || '').toString(),
                        vigencia: this.composeVigencia(p.startDate, p.endDate),
                        estado: this.normaliseEstado(p.status),
                        renovacionAutomatica: this.formatDay(p.renewalDate || p.endDate),
                        branchType: p.branchType || '',
                        policyYear: (p.year || '').toString(),
                        policyId: p.id,
                        isTaker: p.isTaker === true,
                        iconKey: (p.branchType || p.product || '').toString(),
                        acciones: []
                    };
                    card.acciones = this.buildAcciones(card);
                    return card;
                });
            })
            .catch(() => {
                this.loading = false;
                this.insurances = [];
                this.toastr.error(CommonsText.PROBLEMA_SERVICIO_POLIZAS,
                                  CommonsText.PROBLEMA_DE_CONECTIVIDAD,
                                  { timeOut: 5000 });
            });
    }

    private normaliseEstado(raw: any): string {
        if (!raw) { return 'Activo'; }
        const str = raw.toString();
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    }

    private composeVigencia(startDate: any, endDate: any): string {
        const start = this.formatDay(startDate);
        const end = this.formatDay(endDate);
        if (!start && !end) { return ''; }
        return start + ' - ' + end;
    }

    private formatDay(value: any): string {
        if (!value) { return ''; }
        const str = value.toString();
        const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})/);
        if (iso) {
            return iso[3] + '/' + iso[2] + '/' + iso[1];
        }
        const date = new Date(str);
        if (isNaN(date.getTime())) { return str; }
        const pad = (n: number) => (n < 10 ? '0' + n : '' + n);
        return pad(date.getDate()) + '/' + pad(date.getMonth() + 1) + '/' + date.getFullYear();
    }
}
